/** Same-running-tab document attempts. Never persisted, and never hold credentials. */
const records = new Map();
const listeners = new Set();
let revision = 0;
export const subscribeDocumentIntake = listener => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getDocumentIntakeRevision = () => revision;
export const getDocumentIntakeRecord = principalKey => records.get(principalKey) || null;
const immutable = value => {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  if (value instanceof File) return Object.freeze(value);
  if (Array.isArray(value)) return Object.freeze(value.map(immutable));
  return Object.freeze(Object.fromEntries(Object.entries(value).map(([key, item]) => [key, immutable(item)])));
};
const freeze = record => immutable(record);
export const ensureDocumentIntakeRecord = principalKey => {
  if (!principalKey) return null;
  if (!records.has(principalKey)) records.set(principalKey, freeze({ principalKey,
    entries: [], additionalContext: '', started: false, pending: false, message: '' }));
  return records.get(principalKey);
};
export const replaceDocumentIntakeRecord = (previous, patch) => {
  if (!previous || records.get(previous.principalKey) !== previous) return null;
  const next = freeze({ ...previous, ...patch });
  records.set(previous.principalKey, next);
  revision += 1;
  listeners.forEach(listener => listener());
  return next;
};
export const documentEntryAccepted = entry => ['accepted', 'warning'].includes(entry.status);
export const documentBatchUnresolved = record => Boolean(record?.started
  && record.entries.some(entry => !documentEntryAccepted(entry) && entry.status !== 'refused'));
export const documentSelectionError = files => {
  if (!files.length) return 'Please select PDF or image files.';
  if (files.length > 20) return 'Select no more than 20 files at a time. Your existing selection was kept.';
  if (files.some(file => !(file instanceof File) || !file.name
      || !/\.(pdf|png|jpe?g|webp|heic|heif)$/i.test(file.name))) {
    return 'Select only PDF or image files (PNG, JPG, WEBP, HEIC, HEIF). Your existing selection was kept.';
  }
  if (files.some(file => file.size > 5 * 1024 * 1024)) {
    return 'Each file must be 5 MB or smaller. Your existing selection was kept.';
  }
  return null;
};
const unknown = () => ({ status: 'unknown', message: 'We could not confirm whether this order was saved. Keep this tab open and retry the original submission; do not upload it again as a new order.' });
const processing = () => ({ status: 'processing', message: 'This submission is still processing. Wait, then retry the original submission.' });
const refused = () => ({ status: 'refused', message: 'This file was not accepted. Review the file before starting a new selection.' });
const retryDeadline = value => {
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? Date.now() + seconds * 1000 : 0;
};
export const documentFailure = (error, alreadyUnknown = false) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 409 && detail === 'This submission is already being processed') return {
    ...processing(), retryNotBefore: retryDeadline(error.response.headers?.['retry-after']),
  };
  if (!alreadyUnknown && [400, 401, 403, 404, 413, 422].includes(status)) return {
    ...refused(), message: [401, 403].includes(status)
      ? 'This account is not authorized to submit. No acceptance was reported.' : refused().message,
  };
  return { ...unknown(), retryNotBefore: status === 429 ? retryDeadline(error.response.headers?.['retry-after']) : 0 };
};
export const documentAutoRetryEligible = error => {
  const status = error?.response?.status;
  return !error?.invalidDocumentReceipt && (status === 408
    || (status >= 500 && status <= 599)
    || (!error?.response && ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error?.code)));
};
export const documentResult = (results, entry, alreadyUnknown = false) => {
  if (!Array.isArray(results) || results.length !== 1 || results[0]?.filename !== entry.filename
      || typeof results[0]?.success !== 'boolean') return unknown();
  const row = results[0];
  if (row.success) {
    if (typeof row.order?.id !== 'string' || !row.order.id.trim() || row.error != null) return unknown();
    const outcome = row.order.intake_outcome;
    const checks = outcome?.follow_ons;
    const labels = { duplicate_warning: 'duplicate warning check', source_registration: 'source registration',
      subsource_registration: 'subsource registration' };
    if (outcome != null && (outcome.acceptance !== 'accepted' || typeof outcome.replayed !== 'boolean'
        || !checks || Object.keys(labels).some(key => !['completed', 'failed', 'not_rechecked'].includes(checks[key])))) return unknown();
    const unfinished = Object.entries(labels).filter(([key]) => checks?.[key] !== 'completed').map(([, label]) => label);
    const warning = !outcome || outcome.replayed
      || unfinished.length > 0;
    return { status: warning ? 'warning' : 'accepted', receipt: row, file: null,
      message: !outcome ? 'Order saved. This server did not report completion of follow-on checks.'
        : warning ? `Order saved. Completion was not confirmed for: ${unfinished.join(', ') || 'follow-on checks'}. These checks are not automatically retried.`
        : 'Order saved successfully.' };
  }
  if (row.order != null) return unknown();
  if (row.failure_outcome === 'processing' || row.error === 'This submission is already being processed') return {
    ...processing(), retryNotBefore: retryDeadline(row.retry_after_seconds),
  };
  if (row.failure_outcome === 'conflict') return {
    ...unknown(), message: 'The server reported a conflict for this submission. Keep the original selection; do not create a replacement order to bypass it.',
  };
  if (row.failure_outcome === 'refused' && !alreadyUnknown) return refused();
  // Legacy per-file errors do not say whether acceptance already happened.
  // Only exact pre-acceptance validation vocabulary proves a refusal.
  if (!alreadyUnknown && (row.error === 'Unsupported file type. Supported formats: PDF, PNG, JPG, JPEG, WEBP, HEIC, HEIF'
      || (typeof row.error === 'string' && /^File size \(\d+(\.\d+)?MB\) exceeds the 5MB limit\. Please reduce the file size or convert to a smaller format\.$/.test(row.error)))) return refused();
  return unknown();
};
